import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export const useUserStore = defineStore(
    'user',
    () => {
        const user = ref(null);
        const accessToken = ref(localStorage.getItem('accessToken'));
        const refreshToken = ref(localStorage.getItem('refreshToken'));

        const isAuthenticated = computed(() => !!accessToken.value);
        const nombreCompleto = computed(() =>
            user.value ? `${user.value.nombre} ${user.value.apellido}` : ''
        );

        // Backoffice: el usuario tiene al menos un rol BO
        const esBackoffice = computed(() =>
            (user.value?.rolesBO?.length > 0) || false
        );

        // Normalizar: el backend ahora envía objetos {rolBackoffice, nivelAcceso}
        // pero localStorage puede tener el formato viejo (strings)
        const rolesBO = computed(() =>
            (user.value?.rolesBO ?? []).map(r =>
                typeof r === 'string' ? { rolBackoffice: r, nivelAcceso: null } : r
            )
        );

        // Administrador: tiene rol "Administrador" en sus roles BO
        const esAdministrador = computed(() =>
            rolesBO.value.some(r => r.rolBackoffice === 'Administrador')
        );

        // RBAC: niveles de acceso progresivos
        const _niveles = { SoloLectura: 1, Escritura: 2 };

        /**
         * Verifica si el usuario tiene un rol BO específico con nivel mínimo
         * @param {string} rol - Nombre del rol (ej: 'Ganaderia', 'Administrador')
         * @param {string} nivelMinimo - 'SoloLectura' o 'Escritura'
         */
        function tieneRol(rol, nivelMinimo = 'SoloLectura') {
            if (esAdministrador.value) return true;
            const match = rolesBO.value.find(r => r.rolBackoffice === rol);
            if (!match) return false;
            // Administrador no tiene nivelAcceso — siempre pasa
            if (rol === 'Administrador') return true;
            return (_niveles[match.nivelAcceso] ?? 0) >= _niveles[nivelMinimo];
        }

        function puedeVer(rol) {
            return tieneRol(rol, 'SoloLectura');
        }

        function puedeEditar(rol) {
            return tieneRol(rol, 'Escritura');
        }

        function setAuth(authResponse) {
            accessToken.value = authResponse.accessToken;
            refreshToken.value = authResponse.refreshToken;
            localStorage.setItem('accessToken', authResponse.accessToken);
            localStorage.setItem('refreshToken', authResponse.refreshToken);
            user.value = authResponse.usuario;
        }

        async function logout() {
            const rt = refreshToken.value;
            accessToken.value = null;
            refreshToken.value = null;
            user.value = null;
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            if (rt) {
                try {
                    const { authService } = await import('@/service/authService');
                    await authService.logout(rt);
                } catch { /* best effort */ }
            }
        }

        return {
            user,
            accessToken,
            refreshToken,
            isAuthenticated,
            nombreCompleto,
            esBackoffice,
            rolesBO,
            esAdministrador,
            tieneRol,
            puedeVer,
            puedeEditar,
            setAuth,
            logout,
        };
    },
    {
        persist: {
            pick: ['user']
        }
    }
);
